import FormControl from '@material-ui/core/FormControl/index.js';
import FormControlLabel from '@material-ui/core/FormControlLabel/index.js';
import FormLabel from '@material-ui/core/FormLabel/index.js';
import Radio from '@material-ui/core/Radio/index.js';
import RadioGroup from '@material-ui/core/RadioGroup/index.js';
import {makeStyles} from '@material-ui/core/styles/index.js';
import React from 'react';
import PropTypes from 'prop-types';
import RadioButtonsGroup from '../../../../components/form/RadioButtonGroup';
import Typography from '@material-ui/core/Typography';

const useStyles = makeStyles(() => ({
  question: {
    marginBottom: 15,
    paddingTop: 15,
    fontWeight: 'bold',
    fontSize: 16
  },
  formControl: {
    marginTop: 24
  },
  fieldset: {
    width: '100%', //IE fix
    maxWidth: 'inherit'
  },
  formControlNested: {
    marginLeft: 28
  },
}));

const Accessibility = (props) => {
  const classes = useStyles();
  const {handleStepState, form, type} = props;
  const stepSlug = 'accessibility';

  const handleChange = event => handleStepState(stepSlug, {[event.target.name]: event.target.value});

  const getReasonableAdjustment = () => {
    if (type === 'promotion') {
      return <RadioButtonsGroup
        label="Do you intend to request reasonable adjustment under the Equality Act 2010?"
        info={
          <React.Fragment>
            <Typography paragraph={true}>
              For instance, you may require a reasonable adjustment such as extra time or an accommodation such as being able to take a scheduled break during the exam.
            </Typography>
            <Typography paragraph={true}>
              For further information on our reasonable adjustment policy and how you can access support please refer to the College of Policing&apos;s <a href="https://www.college.police.uk/What-we-do/Development/Promotion/Documents/RA%20Policy%20Examinations.pdf" rel="noopener noreferrer" target="_blank">Policy on Accommodations and Reasonable Adjustments</a>.
            </Typography>
          </React.Fragment>}
        showError={form[stepSlug].errors && form[stepSlug].errors.includes('reasonableAdjustment')}
        name="reasonableAdjustment"
        options={[
          {id: 'yes', name: 'Yes'},
          {id: 'no', name: 'No'},
        ]}
        value={form[stepSlug].data.reasonableAdjustment}
        handleChange={(e) => handleChange(e)}
      />;
    } else {
      return <RadioButtonsGroup
        label="Do you intend to request reasonable adjustment under the Equality Act 2010?"
        info={
          <React.Fragment>
            <Typography paragraph={true}>
              For instance, you may require a reasonable adjustment such as extra time.
            </Typography>
            <Typography paragraph={true}>
              For further information on our reasonable adjustment policy and how you can access support please refer to the College of Policing&apos;s <a href={props.policyUrl} rel="noopener noreferrer" target="_blank">Policy on Accommodations and Reasonable Adjustments</a>.
            </Typography>
          </React.Fragment>}
        showError={form[stepSlug].errors && form[stepSlug].errors.includes('reasonableAdjustment')}
        name="reasonableAdjustment"
        options={[
          {id: 'yes', name: 'Yes'},
          {id: 'no', name: 'No'},
        ]}
        value={form[stepSlug].data.reasonableAdjustment}
        handleChange={(e) => handleChange(e)}
      />;
    }
  };

  return (
    <React.Fragment>
      <Typography component="h2" variant="h2">
        Disability
      </Typography>
      <Typography paragraph={true}>
        Do you have any health conditions, learning or communications differences that meet the definition of a disability under the Equality Act 2010?
      </Typography>
      <Typography paragraph={true}>
        Please read the definitions below to understand how your condition(s) might be included. It does not matter whether you have a formal diagnosis.
        The Equality Act 2010 imposes a positive duty on employers to make reasonable adjustments for disabled employees and job applicants.
      </Typography>
      <Typography paragraph={true}>
        You are disabled under this act if you have a physical or mental impairment or neurodivergent condition that has a ‘substantial’ and
        ‘long-term’ negative effect on your ability to perform normal daily activities. What ‘substantial’ and ‘long-term’ mean:
      </Typography>
      <Typography>
        <ul>
          <li>‘substantial’ is more than minor or trivial, for example, it takes much longer than it usually would to complete a daily task like getting dressed</li>
          <li>‘long-term’ means 12 months or more, for example, a breathing condition that develops because of a lung infection</li>
        </ul>
      </Typography>
      <Typography component="h3" variant="h3">
        Progressive conditions
      </Typography>
      <Typography paragraph={true}>
        A progressive condition is one that gets worse over time. People with progressive conditions can be classed as disabled. However,
        you automatically meet the disability definition under the Equality Act 2010 from the day you are diagnosed with human
        immunodeficiency virus (HIV) infection, cancer or multiple sclerosis.
      </Typography>

      <FormControl component="fieldset" className={classes.fieldset}>
        <FormLabel
          component="legend"
          className={classes.question}
          data-testid="label-disability">
          Do you consider yourself to have a disability according to the definition above?
        </FormLabel>
        <div className={(form[stepSlug].errors?.includes('disability') || form[stepSlug].errors?.includes('disabilityCategory')) ? 'inline-error' : ''}>
          {
            (form[stepSlug].errors?.includes('disability') || form[stepSlug].errors?.includes('disabilityCategory')) ?
              <p>If you do not wish to answer this question, please select ‘Prefer not to say’</p> : ''
          }
          <RadioGroup aria-label="disability" name="disability" value={form[stepSlug].data.disability} onChange={(e) => handleChange(e)}>
            <React.Fragment key={'yes'}>
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" />}
                label="Yes"
                data-testid="disability-select-option"
              />
              {form[stepSlug].data.disability === 'yes'
                ? <div className={classes.formControlNested}>
                  <RadioButtonsGroup
                    label="What is the nature of your disability?"
                    name="disabilityCategory"
                    options={[
                      {
                        id: 'adhd', name: 'Attention deficit hyperactivity disorder (ADHD)'
                      },
                      {
                        id: 'asd',
                        name: 'Autistic spectrum disorder (ASD), Aspergers or another condition with differences in communication and interaction'
                      },
                      {
                        id: 'visualImpairment', name: 'Blindness or visual impairment not corrected by glasses'
                      },
                      {
                        id: 'hearingLoss', name: 'Deafness or moderate to severe hearing loss'
                      },
                      {
                        id: 'learningDifference', name: 'Dyslexia, dyspraxia, dyscalculia, dysgraphia or another learning difference'
                      },
                      {
                        id: 'longTermIllness', name: 'Long-term illness such as cancer, HIV, diabetes, chronic heart disease or epilepsy'
                      },
                      {
                        id: 'mentalHealthCondition',
                        name: 'Mental health condition, such as post-traumatic stress disorder (PTSD), depression, schizophrenia or anxiety disorder'
                      },
                      {
                        id: 'physicalDisability',
                        name: 'Physical disability or mobility issue, for example impaired use of arms or legs, use of wheelchair or crutches'
                      },
                      {
                        id: 'neurologicalOrMovementDisorder', name: 'Neurological or movement disorder, tic disorder or Tourette’s syndrome (TS)'
                      },
                      {
                        id: 'otherDisabilityOrHealthCondition',
                        name: 'Another disability, health condition or impairment affecting daily life such as colitis, Chron’s, fibromyalgia, myalgic encephalomyelitis (ME) or motor neurone disease (MND)'
                      },
                      {
                        id: 'other', name: 'No, I do not have any of the above'
                      },
                      {
                        id: 'preferNotToSay', name: 'Prefer not to say'
                      },
                    ]}
                    value={form[stepSlug].data.disabilityCategory}
                    handleChange={(e) => handleChange(e)}
                  />
                </div>
                : null}
            </React.Fragment>
            <FormControlLabel
              value="no"
              control={<Radio color="primary" />}
              label="No"
              data-testid="disability-select-option"
            />
            <FormControlLabel
              value="preferNotToSay"
              control={<Radio color="primary" />}
              label="Prefer not to say"
              data-testid="disability-select-option"
            />
          </RadioGroup>
        </div>
      </FormControl>

      {getReasonableAdjustment()}
    </React.Fragment>
  );
};
Accessibility.propTypes = {
  handleStepState: PropTypes.func,
  type: PropTypes.string,
  form: PropTypes.object,
  step: PropTypes.object,
  policyUrl: PropTypes.string,
};
export default Accessibility;
